import axios from 'axios';

export const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5025';

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Auth APIs
export const authAPI = {
  login: (email: string, password: string) =>
    api.post('/auth/login', { email, password }),
  register: (email: string, password: string, role?: string) =>
    api.post('/auth/register', { email, password, role }),
  getProfile: () => api.get('/auth/profile'),
};

// Players APIs
export const playersAPI = {
  getAll: () => api.get('/players'),
  getById: (id: string) => api.get(`/players/${id}`),
  getDashboard: (id: string, trialPeriodId?: string) => {
    const params = trialPeriodId ? { trialPeriodId } : {};
    return api.get(`/players/${id}/dashboard`, { params });
  },
  create: (data: any) => api.post('/players', data),
  update: (id: string, data: any) => api.patch(`/players/${id}`, data),
  updateProfile: (id: string, data: any) => api.patch(`/players/${id}/profile`, data),
  delete: (id: string) => api.delete(`/players/${id}`),
  uploadPhoto: (id: string, file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/players/${id}/photo`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },
};

// Teams APIs
export const teamsAPI = {
  getAll: () => api.get('/teams'),
  getOne: (id: string) => api.get(`/teams/${id}`),
  getStandings: () => api.get('/teams/standings'),
  create: (data: any) => api.post('/teams', data),
  update: (id: string, data: any) => api.patch(`/teams/${id}`, data),
  delete: (id: string) => api.delete(`/teams/${id}`),
};

// Rankings APIs
export const rankingsAPI = {
  getAll: (season?: string, categoryType?: 'overall' | 'physical' | 'technical', trialPeriodId?: string) => 
    api.get('/rankings', { params: { season, categoryType, trialPeriodId } }),
  getTop: (limit?: number, season?: string) =>
    api.get('/rankings/top', { params: { limit, season } }),
  getByPlayer: (playerId: string) => api.get(`/rankings/player/${playerId}`),
  getOne: (id: string) => api.get(`/rankings/${id}`),
  create: (data: any) => api.post('/rankings', data),
  update: (id: string, data: any) => api.patch(`/rankings/${id}`, data),
  delete: (id: string) => api.delete(`/rankings/${id}`),
};

// Trial Periods APIs
export const trialPeriodsAPI = {
  getAll: () => api.get('/trial-periods'),
  getById: (id: string) => api.get(`/trial-periods/${id}`),
};

// Evaluations APIs
export const evaluationsAPI = {
  getAll: () => api.get('/evaluations'),
  getById: (id: string) => api.get(`/evaluations/${id}`),
  getCategories: () => api.get('/evaluations/categories'),
  getPlayerEvaluations: (playerId: string) => api.get(`/evaluations/player/${playerId}`),
  getPlayerLatestEvaluations: (playerId: string) => api.get(`/evaluations/player/${playerId}/latest`),
  getPlayerEvaluationHistory: (playerId: string) => api.get(`/evaluations/player/${playerId}/history`),
  getCategoryResults: (categoryId: string) => api.get(`/evaluations/category/${categoryId}`),
  getCategoryHistory: (categoryId: string) => api.get(`/evaluations/category/${categoryId}/history`),
  create: (data: any) => api.post('/evaluations', data),
  update: (id: string, data: any) => api.patch(`/evaluations/${id}`, data),
  delete: (id: string) => api.delete(`/evaluations/${id}`),
};

export default api;
